import { PROGRAM_NAMES } from '@/shared/utils/deadline';
import {
    BEING_REVISED_PILL,
    decisionKey,
    pillFor,
} from '@/shared/utils/statusPills';

/**
 * The one map from a course support application's status, and from a
 * program's decision, to the colour and plain-language wording faculty read.
 * The server decides which decisions a viewer may see; this only words what
 * it sent.
 */

const REQUEST_MEANING = Object.freeze({
    draft: 'Not submitted yet.',
    submitted: 'Waiting for the programs you ranked to review it.',
    under_review: 'A program is reviewing it.',
    revision_requested: 'A program asked for changes. Edit and resubmit it.',
    decided:
        'At least one program has decided, and another is still reviewing it or a contingent award is waiting on one.',
    approved: 'A program will support this course.',
    rejected: 'No program you ranked can support this course this term.',
    waitlisted: 'A program has put it on its waitlist.',
    withdrawn: 'Withdrawn, so no program will review it.',
});

export const APPLICATION_STATUS = Object.freeze(
    Object.fromEntries(
        Object.keys(REQUEST_MEANING).map((status) => [
            status,
            { ...pillFor('request', status), meaning: REQUEST_MEANING[status] },
        ])
    )
);

// A request its faculty member took back into revision themselves, as
// opposed to one a program returned
const BEING_REVISED = Object.freeze({
    ...BEING_REVISED_PILL,
    meaning:
        'The faculty member is revising it, so programs cannot decide it until it is resubmitted.',
});

export const isBeingRevisedByFaculty = (application) =>
    application?.status === 'revision_requested' &&
    application.revisionInitiatedBy === 'faculty';

/**
 * The wording for a status. Pass the application too, so a revision the
 * faculty member started reads "Being revised" rather than "Revision
 * Requested".
 */
export function applicationStatusOf(status, application) {
    if (isBeingRevisedByFaculty(application)) return BEING_REVISED;
    return (
        APPLICATION_STATUS[status] || {
            ...pillFor('request', status),
            meaning: null,
        }
    );
}

/**
 * Whether the request has a standing award from a program, by the rule of
 * positionService.standingAward on the server: an approved, partial or
 * contingent decision that is not voided (a pending contingent award stands,
 * and its position is served to the program's coordinators), on a request
 * that is not withdrawn. The staffing read answers 404 otherwise, so only a
 * request with one asks for it.
 */
export function hasStandingAward(application, programType) {
    if (application?.status === 'withdrawn') return false;
    return (application?.decisions || []).some(
        (d) =>
            d.programType === programType &&
            d.finalStatus !== 'voided' &&
            (d.type === 'approved' ||
                d.type === 'partial' ||
                d.type === 'contingent')
    );
}

export function programName(programType) {
    return PROGRAM_NAMES[programType] || String(programType).toUpperCase();
}

const STAFFING_NEXT_STEP =
    'The program will be in touch about staffing this section.';

export function decisionOutcome(decision) {
    const { type, finalStatus, contingentOn } = decision;
    const pill = pillFor('decision', decisionKey(decision));
    switch (type) {
        case 'approved':
        case 'partial':
            return { ...pill, nextStep: STAFFING_NEXT_STEP };
        case 'denied':
            return {
                ...pill,
                nextStep:
                    "Questions about this decision can go to the program's coordinator.",
            };
        case 'waitlisted':
            return {
                ...pill,
                nextStep:
                    'The program may still award support if a place opens.',
            };
        case 'contingent':
            if (finalStatus === 'pending') {
                const higher = contingentOn
                    ? programName(contingentOn)
                    : 'a program you ranked higher';
                return {
                    ...pill,
                    nextStep: `This becomes final if ${higher} does not award support.`,
                };
            }
            if (finalStatus === 'finalized') {
                return { ...pill, nextStep: STAFFING_NEXT_STEP };
            }
            if (finalStatus === 'voided') {
                return {
                    ...pill,
                    nextStep:
                        'A program you ranked higher awarded support, so this award no longer applies.',
                };
            }
            break;
        default:
    }
    return { bg: 'secondary', label: type, nextStep: null };
}

const ROLE_NOUN = { la: 'LAs', si: 'SI leaders', ta: 'TAs' };
const COUNTED_TYPES = new Set(['approved', 'partial', 'contingent']);
const isCount = (value) => Number.isInteger(value) && value >= 1;

export function awardedCountText(decision) {
    const { type, programType, awardedCount, requestedCount } = decision;
    const noun = ROLE_NOUN[programType];
    if (
        !noun ||
        !COUNTED_TYPES.has(type) ||
        !isCount(awardedCount) ||
        !isCount(requestedCount)
    ) {
        return null;
    }
    return `${awardedCount} of ${requestedCount} ${noun} awarded`;
}
